import React from "react";

import { CreditCard as CreditCardIcon } from "lucide-react";

import { getMonthFromString } from "../../helpers/utils";
import VisaCard from "../../../assets/dashboard/visa.svg";
import MasterCard from "../../../assets/dashboard/mastercard.svg";
import DiscoverCard from "../../../assets/dashboard/discover.svg";
import AmericanCard from "../../../assets/dashboard/american-express.svg";

const SavedCardBrandIcon = ({ brand }) => {
  if (brand === "MasterCard")
    return <img src={MasterCard} alt="MasterCard" className="h-7 w-auto" />;
  if (brand === "Visa")
    return <img src={VisaCard} alt="Visa" className="h-7 w-auto" />;
  if (brand === "American Express")
    return (
      <img src={AmericanCard} alt="American Express" className="h-7 w-auto" />
    );
  if (brand === "Discover")
    return <img src={DiscoverCard} alt="Discover" className="h-7 w-auto" />;
  return <CreditCardIcon className="h-6 w-6 text-muted-foreground" />;
};

/**
 * Saved cards to pay with (Tailwind): brand, last 4, DEFAULT / EXPIRED tags and
 * the selected ring. Expired cards can't be picked. Used by the in-app checkout
 * and the invited homeowner's checkout.
 */
export const SavedCardList = ({
  cards = [],
  selectedCardId = null,
  defaultCardId = null,
  onSelect = () => {},
  className = "space-y-3 mb-4",
}) => (
  <div className={className}>
    {cards.map((card) => {
      const expired = isSavedCardExpired(card);
      const isSelected = selectedCardId != null && selectedCardId === card.id;
      const isDefault = defaultCardId
        ? card.id === parseInt(defaultCardId, 10)
        : card.default === 1;

      return (
        <button
          key={card.id}
          type="button"
          disabled={expired}
          onClick={() => onSelect(card)}
          className={`w-full min-w-0 text-left p-2.5 sm:p-4 rounded-xl border transition-colors ${
            expired
              ? "opacity-50 cursor-not-allowed bg-muted/20 border-border/40"
              : isSelected
                ? "bg-secondary/5 border-secondary ring-1 ring-secondary/30"
                : "bg-muted/30 border-border/50 hover:border-secondary/40"
          }`}
        >
          <div className="flex items-start gap-3 min-w-0">
            <span
              className={`mt-0.5 h-4 w-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
                isSelected ? "border-secondary" : "border-muted-foreground/40"
              }`}
            >
              {isSelected ? (
                <span className="h-2 w-2 rounded-full bg-secondary" />
              ) : null}
            </span>
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-10 h-8 sm:w-12 sm:h-10 rounded flex items-center justify-center shrink-0">
                  <SavedCardBrandIcon brand={card.brand} />
                </div>
                <span className="text-foreground uppercase text-sm font-medium break-words [word-break:normal]">
                  {card.brand} - **** {card.last4}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {isDefault ? (
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs rounded-full font-medium whitespace-nowrap shrink-0">
                    DEFAULT
                  </span>
                ) : null}
                {expired ? (
                  <span className="px-2 py-0.5 bg-destructive/10 text-destructive text-xs rounded-full font-medium whitespace-nowrap shrink-0">
                    EXPIRED
                  </span>
                ) : null}
                <span className="text-muted-foreground text-xs sm:text-sm whitespace-nowrap">
                  Exp. {card.exp_month} {card.exp_year}
                </span>
              </div>
            </div>
          </div>
        </button>
      );
    })}
  </div>
);

export const isSavedCardExpired = (card) => {
  if (!card?.id) return true;
  const year = Number(card.exp_year ?? 0);
  if (year > new Date().getFullYear()) return false;
  if (year < new Date().getFullYear()) return true;
  return (
    getMonthFromString(card.exp_month, card.exp_year) < new Date().getMonth() + 1
  );
};

const isDefaultCard = (card) =>
  Number(card?.default) === 1 ||
  card?.is_default === true ||
  Number(card?.is_default) === 1;

/**
 * Card to charge: the requested or default card when it is still valid,
 * otherwise the first unexpired card.
 */
export const cardToSelect = (cards, defaultCardId = null) => {
  const list = Array.isArray(cards) ? cards : [];
  if (list.length === 0) return null;

  const preferId = defaultCardId
    ? parseInt(String(defaultCardId), 10)
    : list.find((item) => isDefaultCard(item))?.id;
  const preferred = list.find((item) => Number(item?.id) === Number(preferId));
  if (preferred && !isSavedCardExpired(preferred)) return preferred;

  return list.find((item) => !isSavedCardExpired(item)) || null;
};

/**
 * How the payment screen should react to a save-card response.
 * kind: "saved" | "saved-no-id" | "duplicate" | "error"
 */
/**
 * Continue-client payment must save and list the buyer's card.
 * A customer's own checkout, and an agent's personal properties, stay on their own card list.
 */
export const clientPaymentCardTarget = ({
  isServiceProvider = false,
  isAdminCreateUser = false,
  uid = null,
  invToken = null,
} = {}) => {
  const buyerId = uid || null;
  const payForBuyer = Boolean(isServiceProvider && isAdminCreateUser && buyerId);
  return {
    uid: payForBuyer ? buyerId : undefined,
    refreshSavedCards: Boolean(invToken || payForBuyer),
  };
};

export const savedCardOutcome = (response) => {
  const message = String(response?.data?.message || response?.message || "");
  const id = response?.data?.id || response?.data?.card_id || null;
  if (response?.success && id) return { kind: "saved", id };
  if (response?.success) return { kind: "saved-no-id" };
  if (/already (saved|added)/i.test(message)) return { kind: "duplicate" };
  return { kind: "error", message };
};
